import { type FigmaTextStyle, typographyStyles } from '../tokens/typography-styles'

const ROOT_FONT_SIZE = 16

const WEIGHT_CLASS: Record<FigmaTextStyle['weight'], string> = {
    Regular: 'font-regular',
    Medium: 'font-medium',
    SemiBold: 'font-semiBold',
}

/**
 * Both size and line height are rem, never a ratio or a percentage. Every Figma
 * value is a whole multiple of 1/16 px, so rem is exact — leading-[123%] renders
 * D1 at 63.96px instead of 64, and drifts on most other steps too.
 */
const rem = (px: number): string => `${Number((px / ROOT_FONT_SIZE).toFixed(4))}rem`

export const figmaKeyFor = (alias: string): string =>
    (alias.split('/').pop() ?? alias).trim().toLowerCase()

export const computeClasses = (style: FigmaTextStyle): string => {
    const weight = WEIGHT_CLASS[style.weight]
    if (!weight) {
        throw new Error(
            `aos-ds typography: Figma style "${style.alias}" uses weight "${style.weight}", ` +
                `which maps to no Manrope family (known: ${Object.keys(WEIGHT_CLASS).join(', ')}).`,
        )
    }
    return [
        `text-[${rem(style.size)}]`,
        `leading-[${rem(style.lineHeight)}]`,
        weight,
        ...(style.upper ? ['uppercase'] : []),
    ].join(' ')
}

export const typographyVariants = Object.fromEntries(
    Object.entries(typographyStyles as Record<string, FigmaTextStyle>).map(([key, style]) => [
        key,
        style.classes,
    ]),
) as Record<keyof typeof typographyStyles, string>

export type TypographyVariant = keyof typeof typographyStyles

export const typographyVariantNames = Object.keys(typographyStyles) as TypographyVariant[]

export const figmaStyleFor = (variant: TypographyVariant): FigmaTextStyle =>
    typographyStyles[variant] as FigmaTextStyle

/**
 * A component contract records the Figma style id its label uses, so the label
 * renders the style the design binds rather than one picked to look right.
 */
export const variantForStyleId = (id: string): TypographyVariant => {
    const match = typographyVariantNames.find(variant => figmaStyleFor(variant).id === id)
    if (!match) {
        throw new Error(
            `aos-ds typography: no variant for Figma text style "${id}" — ` +
                're-run `yarn tokens:import --aos-ds`, the mirror is behind the file.',
        )
    }
    return match
}
